import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import {
  useCurrentUser,
  userHasGroup,
  userIsAbrechnung,
  userIsBuero,
  userIsRealAppAdmin,
} from "@/lib/auth";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  deleteDokument,
  DOKUMENT_STATUS_BADGE,
  DOKUMENT_STATUS_LABELS,
  isStuecklisteListItem,
  listDokumente,
  type DokumentListItem,
} from "@/lib/dokumente";
import { deleteStueckliste } from "@/lib/stuecklisten";
import {
  formatMahnungDate,
  listMahnungen,
  mahnungStatusLabel,
  type MahnungListItem,
} from "@/lib/mahnwesen";
import { useRealtime } from "@/lib/useRealtime";
import { useDocumentCreate } from "@/pages/dokumente/useDocumentCreate";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";
import {
  appDocumentRowSubtitle,
  appDocumentRowTitle,
  collapseAppDocumentRevisions,
  groupCollapsedAppDocuments,
  isNativeAppOverviewDocument,
} from "@/projectOverview/appDocumentsDisplay";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";

export function AppDocumentsSection({ orderId }: { orderId: number }) {
  const { user } = useCurrentUser();
  const canDeleteDrafts = Boolean(
    user &&
      (userIsBuero(user) ||
        userIsAbrechnung(user) ||
        userIsRealAppAdmin(user) ||
        userHasGroup(user, "App-Admins")),
  );
  const { open: openDocumentCreate } = useDocumentCreate();
  const { open: openDocumentEditor } = useDocumentEditor();
  const { open: openProjectOverview } = useProjectOverview();
  const navigate = useNavigate();
  const [docs, setDocs] = useState<DokumentListItem[] | null>(null);
  const [mahnungen, setMahnungen] = useState<MahnungListItem[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    const [list, mahnungenListe] = await Promise.all([
      listDokumente({ auftrag_id: orderId })
        .then((rows) => (rows || []).filter(isNativeAppOverviewDocument))
        .catch(() => [] as DokumentListItem[]),
      listMahnungen({ auftrag_id: orderId, limit: 100 }).catch(() => [] as MahnungListItem[]),
    ]);
    return { list, mahnungenListe };
  }, [orderId]);

  useEffect(() => {
    let cancelled = false;
    setDocs(null);
    void reload().then(({ list, mahnungenListe }) => {
      if (!cancelled) {
        setDocs(list);
        setMahnungen(mahnungenListe);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  useRealtime({
    scopes: [`auftraege:${orderId}`, "dokumente:list"],
    domains: ["dokumente", "hero", "auftraege"],
    events: ["dokumente_changed", "dokument_status_changed", "hero_changed", "auftraege_changed"],
    enabled: !!orderId,
    onChange: () => {
      void reload().then(({ list, mahnungenListe }) => {
        setDocs(list);
        setMahnungen(mahnungenListe);
      });
    },
  });

  const groups = useMemo(
    () => groupCollapsedAppDocuments(collapseAppDocumentRevisions(docs || [])),
    [docs],
  );

  async function deleteDraft(doc: DokumentListItem) {
    const label = String(doc.display_number || doc.dokument_nummer || "Entwurf").trim();
    if (
      !window.confirm(
        `Entwurf „${label}“ löschen? Nur dieses Dokument wird entfernt, das Projekt bleibt erhalten.`,
      )
    ) {
      return;
    }
    setDeletingId(doc.id);
    try {
      if (isStuecklisteListItem(doc)) {
        const stuecklisteId = Number(doc.stueckliste_id || 0);
        if (!stuecklisteId) throw new Error("Stückliste ohne ID.");
        await deleteStueckliste(stuecklisteId);
      } else {
        await deleteDokument(doc.id);
      }
      toast.success("Entwurf gelöscht.");
      const next = await reload();
      setDocs(next.list);
      setMahnungen(next.mahnungenListe);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  }

  function openDoc(doc: DokumentListItem) {
    if (isStuecklisteListItem(doc)) {
      const slId = Number(doc.stueckliste_id || 0);
      openProjectOverview(orderId, {
        openMontageunterlagen: true,
        stuecklisteId: slId > 0 ? slId : undefined,
      });
      return;
    }
    openDocumentEditor({ docId: doc.id });
  }

  return (
    <OverviewCard title="App-Dokumente">
      <div className="mb-3 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => openDocumentCreate({ auftragId: orderId, typ: "angebot" })}
        >
          Neu
        </Button>
      </div>

      {docs === null ? (
        <p className="text-sm text-label-tertiary">Lade …</p>
      ) : !groups.length && !mahnungen.length ? (
        <p className="text-sm text-label-tertiary">Keine App-Dokumente vorhanden.</p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <section key={group.key}>
              <h4 className="eh-po-doc-group-title">{group.label}</h4>
              <ul className="eh-po-timesheet-list">
                {group.items.map(({ doc, olderCount }) => (
                  <li key={`${doc.quelle || "gd"}-${doc.stueckliste_id ?? doc.id}`}>
                    <div className="eh-po-timesheet-row eh-po-doc-row">
                      <button
                        type="button"
                        onClick={() => openDoc(doc)}
                        className="eh-po-timesheet-meta min-w-0 flex-1 text-left"
                      >
                        <div className="eh-po-timesheet-title">
                          <span>{appDocumentRowTitle(doc)}</span>
                          <span
                            className={clsx(
                              "eh-po-timesheet-badge",
                              DOKUMENT_STATUS_BADGE[doc.status],
                            )}
                          >
                            {DOKUMENT_STATUS_LABELS[doc.status]}
                          </span>
                        </div>
                        <div className="eh-po-timesheet-subtitle">
                          {appDocumentRowSubtitle(doc, olderCount)}
                        </div>
                      </button>
                      {canDeleteDrafts && doc.status === "entwurf" ? (
                        <button
                          type="button"
                          className="eh-po-timesheet-action text-status-red"
                          disabled={deletingId === doc.id}
                          onClick={() => void deleteDraft(doc)}
                        >
                          Entwurf löschen
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {mahnungen.length ? (
            <section>
              <h4 className="eh-po-doc-group-title">Mahnungen</h4>
              <ul className="eh-po-timesheet-list">
                {mahnungen.map((m) => (
                  <li key={`mahnung-${m.id}`}>
                    <div className="eh-po-timesheet-row eh-po-doc-row">
                      <button
                        type="button"
                        onClick={() => navigate(`/dokumente/mahnwesen/${m.id}`)}
                        className="eh-po-timesheet-meta min-w-0 flex-1 text-left"
                      >
                        <div className="eh-po-timesheet-title">
                          <span>{m.nummer || m.stufe_bezeichnung}</span>
                          <span className="eh-po-timesheet-badge">{mahnungStatusLabel(m.status, true)}</span>
                        </div>
                        <div className="eh-po-timesheet-subtitle">
                          {[m.stufe_bezeichnung, m.rechnungsnummern, formatMahnungDate(m.belegdatum)]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </OverviewCard>
  );
}
